import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { useLanguage } from './LanguageContext';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const { t } = useLanguage();
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [guest, setGuest] = useState(() => {
    const stored = localStorage.getItem('auth_guest');
    return stored ? JSON.parse(stored) : null;
  });
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState(null);
  const loginInProgress = useRef(false);

  const isAuthenticated = !!token && !!guest;

  const login = async (name, password) => {
    setSessionExpiredMessage(null);
    loginInProgress.current = true;
    try {
      const res = await api.post('/api/auth/login', { name, password });
      const { token: newToken, guest: guestData } = res.data;
      localStorage.setItem('auth_token', newToken);
      localStorage.setItem('auth_guest', JSON.stringify(guestData));
      setToken(newToken);
      setGuest(guestData);
      return guestData;
    } finally {
      loginInProgress.current = false;
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_guest');
    setToken(null);
    setGuest(null);
  }, []);

  const updateGuest = useCallback((guestData) => {
    localStorage.setItem('auth_guest', JSON.stringify(guestData));
    setGuest(guestData);
  }, []);

  // Listen for 401 token expiry events from the API interceptor.
  // Ignore the event during an active login attempt — a 401 there means wrong
  // password, not an expired session, and LoginScreen handles it separately.
  useEffect(() => {
    const handler = () => {
      if (loginInProgress.current) return;
      logout();
      setSessionExpiredMessage(t('sessionExpired'));
    };
    window.addEventListener('auth-expired', handler);
    return () => window.removeEventListener('auth-expired', handler);
  }, [logout, t]);

  return (
    <AuthContext.Provider
      value={{ token, guest, isAuthenticated, login, logout, updateGuest, sessionExpiredMessage }}
    >
      {children}
    </AuthContext.Provider>
  );
};
